import { getRoutePath } from '../utils/domain';

declare global {
  interface Window {
    /**
     * The App-Proxy skeleton's injected blob. Declaration-merged with the copy in
     * landing-page's `routePath.ts`; TypeScript requires the two to be
     * IDENTICAL, so the D-42 fields below must be kept in sync with it. (They
     * drifted once — `vite build` does not typecheck, so the mismatch only
     * showed up under an explicit `tsc --noEmit`.)
     */
    __BC_BOOTSTRAP__?: {
      shop?: string | null;
      loggedInCustomerId?: string | null;
      customerAccountsVersion?: 'new' | 'classic' | null;
      authType?: 'stackline' | 'shopify' | 'no_auth' | null;
      customerFirstName?: string | null;
      [key: string]: unknown;
    };
  }
}

/**
 * Per-tab debounce key of the old client-side Shopify-login trigger. Nothing
 * sets it now. Explicit sign-in and BC sign-out still clear it.
 */
export const SHOPIFY_LOGIN_ATTEMPT_KEY = 'bc-shopify-login-attempted';

/** Clear the per-tab auto-SSO debounce (safe when sessionStorage is unavailable). */
export function clearShopifyLoginAttempt(): void {
  try {
    window.sessionStorage.removeItem(SHOPIFY_LOGIN_ATTEMPT_KEY);
  } catch {
    // sessionStorage unavailable — ignore.
  }
}

/**
 * Where to send a signed-out shopper who asks the concierge to sign them in,
 * for hosts that cannot run the inline phone/OTP form themselves.
 *
 * The inline form lives in the landing-page bundle (it needs Amplify + the
 * Redux profile reload), so the EMBED bubble — which mounts the same concierge
 * from `bc-checkout-shop-app` — has no `inlineSignIn` to call. Without a
 * destination, `PromptLoginPart` rendered nothing at all and a shopper who
 * asked the bubble about an order was told to "sign in here" with nothing to
 * click. This is that "here".
 *
 * `shopify` → the store's own customer login, returning to the page the
 * shopper is on (same classic/new split as landing-page `getShopifyLoginUrl`;
 * new customer accounts ignore classic's `return_url`).
 * `stackline` → the Brandclub phone-OTP page, under the App-Proxy sub-path
 * when there is one (D-44 serves the whole login state machine there).
 *
 * Returns null when there is nowhere to go — `no_auth` stores have no sign-in.
 */
export function getConciergeSignInUrl(authType?: 'stackline' | 'shopify' | 'no_auth' | null): string | null {
  if (typeof window === 'undefined') return null;
  const resolved = authType ?? window.__BC_BOOTSTRAP__?.authType ?? null;
  if (resolved === 'no_auth') return null;
  if (resolved === 'shopify') {
    const version = window.__BC_BOOTSTRAP__?.customerAccountsVersion;
    const entry = version === 'classic' ? '/account/login?return_url=' : '/customer_authentication/login?return_to=';
    return `${entry}${encodeURIComponent(`${window.location.pathname}${window.location.search}`)}`;
  }
  return `${getRoutePath()}/signin`;
}
